/* BD Quiz Hero: five-question quiz, recommendation and result page */
(() => {
  'use strict';

  const LAST_QUESTION = 5;
  const SINCE_STEPS = ['Tage', 'Wochen', 'Monate', 'Jahre'];
  const DEFAULT_SINCE = 1;
  // Storefront API used to price the result page with Shopify's real discounts (automatic tiers, discount code).
  const DEFAULT_API_VERSION = '2026-04';
  const PRICING_QUERY = `mutation BdQuizPricing($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        cost { subtotalAmount { amount } totalAmount { amount } }
        discountCodes { code applicable }
        discountAllocations {
          discountedAmount { amount }
          ... on CartAutomaticDiscountAllocation { title }
          ... on CartCodeDiscountAllocation { code }
        }
      }
      userErrors { message }
    }
  }`;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  class BDQuiz {
    constructor() {
      this.overlay = document.querySelector('[data-bd-quiz]');
      this.config = JSON.parse(document.querySelector('[data-bd-quiz-config]').textContent);
      this.templates = {};
      document.querySelectorAll('template[data-bd-tpl]').forEach((tpl) => {
        this.templates[tpl.dataset.bdTpl] = tpl;
      });

      this.state = this.freshState();
      this.cartQty = new Map();
      this.cartQueue = Promise.resolve();
      this.discountRequested = false;
      this.pricingCache = new Map();
      this.lastFocus = null;

      // Portal to <body>: a transformed ancestor would otherwise break position: fixed.
      document.body.appendChild(this.overlay);

      this.cacheDom();
      this.bind();
      this.syncUI();
    }

    destroy() {
      this.close(true);
      this.overlay.remove();
      document.removeEventListener('keydown', this.onKeydown);
    }

    freshState() {
      return {
        step: 1,
        view: 'bundle',
        name: '',
        age: null,
        obs: [],
        prevent: false,
        since: DEFAULT_SINCE,
        wish: null,
      };
    }

    cacheDom() {
      const o = this.overlay;
      this.nameInput = o.querySelector('[data-bd-name-input]');
      this.backBtn = o.querySelector('[data-bd-back]');
      this.closeBtn = o.querySelector('[data-bd-close]');
      this.progress = o.querySelector('[data-bd-progress]');
      this.screens = [...o.querySelectorAll('[data-bd-step]')];
      this.slider = o.querySelector('[data-bd-slider]');
      this.range = o.querySelector('[data-bd-range]');
      this.resultEl = o.querySelector('[data-bd-result]');
    }

    bind() {
      const o = this.overlay;

      this.backBtn.addEventListener('click', () => this.back());
      this.closeBtn.addEventListener('click', () => this.close());

      o.querySelectorAll('[data-bd-next]').forEach((btn) => btn.addEventListener('click', () => this.next()));

      this.nameInput.addEventListener('input', () => {
        this.state.name = this.nameInput.value;
        this.updateNext();
      });
      this.nameInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && this.canContinue()) {
          event.preventDefault();
          this.next();
        }
      });

      o.querySelectorAll('[data-bd-age]').forEach((card) =>
        card.addEventListener('click', () => {
          this.state.age = card.dataset.bdAge;
          this.syncUI();
        })
      );

      o.querySelectorAll('[data-bd-obs]').forEach((card) =>
        card.addEventListener('click', () => {
          const id = card.dataset.bdObs;
          const list = this.state.obs;
          // Selecting an observation clears the exclusive "nothing of that" answer.
          this.state.prevent = false;
          this.state.obs = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
          this.syncUI();
        })
      );

      o.querySelector('[data-bd-prevent]').addEventListener('click', () => {
        // Selecting "nothing of that" clears all other selections (and toggles itself off on a second tap).
        this.state.prevent = !this.state.prevent;
        this.state.obs = [];
        this.syncUI();
      });

      this.range.addEventListener('input', () => {
        this.state.since = Number(this.range.value);
        this.syncUI();
      });
      o.querySelectorAll('[data-bd-tick]').forEach((tick) =>
        tick.addEventListener('click', () => {
          this.state.since = Number(tick.dataset.bdTick);
          this.syncUI();
        })
      );

      o.querySelectorAll('[data-bd-wish]').forEach((card) =>
        card.addEventListener('click', () => {
          this.state.wish = card.dataset.bdWish;
          this.syncUI();
        })
      );

      this.onResultResize = () => this.positionBelowHeader();
      this.onKeydown = (event) => this.handleKeydown(event);
      document.addEventListener('keydown', this.onKeydown);
    }

    /* ---------------------------------------------------------------- navigation */

    get isOpen() {
      return !this.overlay.hidden;
    }

    // When started from the menu drawer: close the drawer first, so the quiz does not open on top of it.
    closeMenuDrawer(trigger, event) {
      const drawer = trigger && trigger.closest ? trigger.closest('header-drawer') : null;
      if (!drawer) return null;
      const summary = drawer.querySelector('summary');
      const details = drawer.querySelector('details');
      if (details && details.hasAttribute('open')) {
        if (typeof drawer.closeMenuDrawer === 'function' && event) {
          drawer.closeMenuDrawer(event, summary);
        } else {
          details.removeAttribute('open');
        }
      }
      return summary;
    }

    open(trigger, event) {
      const drawerSummary = this.closeMenuDrawer(trigger, event);
      this.lastFocus = drawerSummary || trigger || document.activeElement;
      if (!this.isOpen) this.scrollBefore = window.scrollY;
      this.overlay.hidden = false;
      document.documentElement.classList.add('bd-quiz-open');
      this.go(1);
    }

    close(silent) {
      if (!this.isOpen) return;
      this.overlay.hidden = true;
      this.leaveResultLayout();
      document.documentElement.classList.remove('bd-quiz-open');
      if (typeof this.scrollBefore === 'number') window.scrollTo({ top: this.scrollBefore, behavior: 'instant' });
      if (!silent && this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus({ preventScroll: true });
    }

    back() {
      if (typeof this.state.step === 'number' && this.state.step > 1) {
        this.go(this.state.step - 1);
      } else {
        this.close();
      }
    }

    next() {
      if (!this.canContinue()) return;
      if (this.state.step === 1) this.state.name = this.state.name.trim();
      if (this.state.step === LAST_QUESTION) {
        this.showResult();
      } else {
        this.go(this.state.step + 1);
      }
    }

    go(step) {
      this.state.step = step;
      this.screens.forEach((screen) => {
        screen.hidden = screen.dataset.bdStep !== String(step);
      });

      const isResult = step === 'result';
      this.overlay.dataset.bdMode = isResult ? 'result' : 'question';
      if (isResult) {
        this.enterResultLayout();
      } else {
        this.leaveResultLayout();
      }
      this.progress.hidden = isResult;
      this.backBtn.style.visibility = isResult ? 'hidden' : '';
      this.syncUI();
      this.overlay.scrollTo({ top: 0 });

      const screen = this.overlay.querySelector(`[data-bd-step="${step}"]`);
      const focusTarget = step === 1 ? this.nameInput : screen.querySelector('h2');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }

    // Result page: the site's own header (menu, logo, search, cart) stays usable above the quiz.
    // The overlay starts below the header and sits under its z-index, so the menu drawer, search and cart work as usual.
    enterResultLayout() {
      document.documentElement.classList.add('bd-quiz-result');
      window.scrollTo({ top: 0, behavior: 'instant' });
      this.positionBelowHeader();
      if (!this.resultResizeBound) {
        this.resultResizeBound = true;
        window.addEventListener('resize', this.onResultResize);
      }
    }

    leaveResultLayout() {
      document.documentElement.classList.remove('bd-quiz-result');
      this.overlay.style.top = '';
      if (this.resultResizeBound) {
        this.resultResizeBound = false;
        window.removeEventListener('resize', this.onResultResize);
      }
    }

    positionBelowHeader() {
      const header = document.querySelector('.section-header');
      const bottom = header ? Math.round(header.getBoundingClientRect().bottom) : 0;
      this.overlay.style.top = `${Math.max(bottom, 0)}px`;
    }

    handleKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        // Let the cart drawer close first when it sits on top of the quiz.
        if (document.querySelector('cart-drawer.active')) return;
        this.close();
        return;
      }

      if (event.key !== 'Tab' || !this.overlay.contains(event.target)) return;
      const items = [...this.overlay.querySelectorAll(FOCUSABLE)].filter((node) => node.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && event.target === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && event.target === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---------------------------------------------------------------- question state */

    canContinue() {
      const s = this.state;
      switch (s.step) {
        case 1:
          return s.name.trim().length > 0;
        case 2:
          return Boolean(s.age);
        case 3:
          return s.obs.length > 0 || s.prevent;
        case 4:
          return true;
        case 5:
          return Boolean(s.wish);
        default:
          return false;
      }
    }

    updateNext() {
      const screen = this.overlay.querySelector(`[data-bd-step="${this.state.step}"]`);
      const btn = screen && screen.querySelector('[data-bd-next]');
      if (btn) btn.disabled = !this.canContinue();
    }

    syncUI() {
      const o = this.overlay;
      const s = this.state;
      const questionNumber = typeof s.step === 'number' ? s.step : LAST_QUESTION;

      // Name placeholders: inserted unchanged, never as HTML.
      o.querySelectorAll('[data-bd-name]').forEach((node) => {
        node.textContent = s.name;
      });
      if (this.nameInput.value !== s.name) this.nameInput.value = s.name;

      // Progress: each question fills its own heart. The line to the reward heart fills with question 5.
      o.querySelectorAll('[data-bd-heart]').forEach((heart) => {
        heart.classList.toggle('is-on', Number(heart.dataset.bdHeart) <= questionNumber);
      });
      o.querySelectorAll('[data-bd-track]').forEach((track) => {
        const i = Number(track.dataset.bdTrack);
        track.classList.toggle('is-on', i < questionNumber || (i === LAST_QUESTION && questionNumber === LAST_QUESTION));
      });

      o.querySelectorAll('[data-bd-age]').forEach((card) =>
        card.setAttribute('aria-checked', String(card.dataset.bdAge === s.age))
      );
      o.querySelectorAll('[data-bd-obs]').forEach((card) =>
        card.setAttribute('aria-checked', String(s.obs.includes(card.dataset.bdObs)))
      );
      o.querySelector('[data-bd-prevent]').setAttribute('aria-checked', String(s.prevent));
      o.querySelectorAll('[data-bd-wish]').forEach((card) =>
        card.setAttribute('aria-checked', String(card.dataset.bdWish === s.wish))
      );

      this.range.value = String(s.since);
      this.range.setAttribute('aria-valuetext', SINCE_STEPS[s.since]);
      this.slider.style.setProperty('--bdq-p', String(s.since / (SINCE_STEPS.length - 1)));
      o.querySelectorAll('[data-bd-tick]').forEach((tick) =>
        tick.classList.toggle('is-active', Number(tick.dataset.bdTick) === s.since)
      );

      this.updateNext();
    }

    reset() {
      this.state = this.freshState();
      this.go(1);
    }

    /* ---------------------------------------------------------------- result */

    get ageLabel() {
      const card = this.overlay.querySelector(`[data-bd-age="${this.state.age}"]`);
      return card ? `${card.dataset.label}, ${card.dataset.range}` : '';
    }

    recommend() {
      const s = this.state;
      const senior = s.age === 'senior' || s.age === 'old';
      const picked = this.config.observations.filter((obs) => {
        if (!obs.product) return false;
        if (s.prevent) return obs.preventionAll || (senior && obs.preventionSenior);
        return s.obs.includes(obs.id);
      });

      // Each observation maps to exactly one product, no duplicates. Block order = matrix order.
      const seen = new Set();
      return picked
        .map((obs) => obs.product)
        .filter((product) => {
          if (seen.has(product.variantId)) return false;
          seen.add(product.variantId);
          return true;
        });
    }

    money(cents) {
      const number = (cents / 100).toLocaleString(this.config.locale || 'de', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `${number}${this.config.currencySymbol}`;
    }

    clone(name) {
      return this.templates[name].content.firstElementChild.cloneNode(true);
    }

    showResult() {
      const s = this.state;
      s.view = 'bundle';
      this.products = this.recommend();

      const view = this.clone('result');
      this.resultEl.replaceChildren(view);
      this.resultView = view;

      const discountAmount = this.config.discountCents / 100;
      view.querySelector('[data-bd-discount-value]').textContent = Number.isInteger(discountAmount)
        ? `${discountAmount}${this.config.currencySymbol}`
        : this.money(this.config.discountCents);
      view.querySelector('[data-bd-code]').textContent = this.config.discountCode;

      view.querySelector('[data-bd-profile-name]').textContent = s.name;
      view.querySelector('[data-bd-profile-age]').textContent = this.ageLabel;
      view.querySelectorAll('[data-bd-avatar]').forEach((cat) => {
        cat.hidden = cat.dataset.bdAvatar !== s.age;
      });
      view.querySelector('[data-bd-fact-since]').textContent = SINCE_STEPS[s.since];
      view.querySelector('[data-bd-fact-wish]').textContent = s.wish;

      const chips = view.querySelector('[data-bd-chips]');
      const chipTexts = s.prevent
        ? [this.config.preventionChip]
        : this.config.observations.filter((obs) => s.obs.includes(obs.id)).map((obs) => obs.chip);
      chipTexts.filter(Boolean).forEach((text) => chips.appendChild(el('span', 'bd-quiz__chip', text)));

      view.querySelector('[data-bd-restart]').addEventListener('click', () => this.reset());

      this.codeBand = view.querySelector('[data-bd-code-band]');
      this.pricing = undefined;
      this.pricingPromise = this.getPricing(this.products).then((pricing) => {
        this.pricing = pricing;
        // Shopify refuses the code for this cart (conditions, or it cannot combine with the automatic tier discount):
        // do not claim it is activated.
        if (this.codeBand) this.codeBand.hidden = Boolean(pricing && pricing.codeApplicable === false);
        return pricing;
      });

      this.renderOffer();
      this.go('result');

      this.applyDiscount();
      this.refreshCartQty().then(() => {
        if (this.state.view === 'single') this.renderOffer();
      });
    }

    renderOffer() {
      const host = this.resultView.querySelector('[data-bd-offer]');
      if (!this.products.length) {
        host.replaceChildren(this.renderEmpty());
      } else if (this.state.view === 'single' && this.products.length > 1) {
        host.replaceChildren(this.renderSingles());
      } else {
        host.replaceChildren(this.renderBundle());
      }
      this.overlay.querySelectorAll('[data-bd-name]').forEach((node) => {
        node.textContent = this.state.name;
      });
    }

    renderEmpty() {
      const box = el('div', 'bd-quiz__bundle');
      box.appendChild(el('p', 'bd-quiz__price-label', 'Für diese Auswahl haben wir gerade keine Empfehlung.'));
      return box;
    }

    setBadge(root, number) {
      const badge = root.querySelector('[data-bd-badge]');
      if (!badge) return;
      if (number == null) {
        badge.remove();
      } else {
        badge.querySelector('[data-bd-badge-num]').textContent = String(number);
      }
    }

    renderBundle() {
      const products = this.products;
      const count = products.length;
      const view = this.clone('bundle');

      const packshots = view.querySelector('[data-bd-packshots]');
      packshots.style.setProperty('--bdq-cols-m', String(Math.min(count, 2)));
      packshots.style.setProperty('--bdq-cols-d', String(Math.min(count, 4)));
      products.forEach((product, index) => {
        const shot = this.clone('packshot');
        const img = shot.querySelector('[data-bd-packshot-img]');
        if (product.image) {
          img.src = product.image;
          img.alt = product.name;
        } else {
          img.remove();
        }
        // A single product carries no number.
        this.setBadge(shot, count > 1 ? index + 1 : null);
        packshots.appendChild(shot);
      });

      // The price comes from Shopify (cart pricing with all automatic discounts and the code), so it stays in sync
      // when the discount tiers change. Until it arrives only a placeholder is shown.
      view.querySelector('[data-bd-price-label]').textContent = count > 1 ? 'Bundle-Preis' : 'Preis';
      const priceOld = view.querySelector('[data-bd-price-old]');
      const priceNew = view.querySelector('[data-bd-price-new]');
      priceOld.hidden = true;
      priceNew.textContent = '–';
      const listSum = products.reduce((total, product) => total + product.price, 0);
      this.pricingPromise.then((pricing) => {
        if (!view.isConnected) return;
        if (pricing) {
          priceOld.textContent = this.money(pricing.subtotal);
          priceOld.hidden = pricing.subtotal <= pricing.total;
          priceNew.textContent = this.money(pricing.total);
        } else {
          // Shopify could not be reached: show the list price only, never a discount we cannot confirm.
          priceOld.hidden = true;
          priceNew.textContent = this.money(listSum);
        }
      });

      const buy = view.querySelector('[data-bd-buy]');
      buy.textContent = count > 1 ? 'Im Bundle kaufen' : 'In den Warenkorb';
      const available = products.filter((product) => product.available);
      buy.disabled = available.length === 0;
      buy.addEventListener('click', () => this.buyBundle(buy, available));

      const single = view.querySelector('[data-bd-single]');
      if (count > 1) {
        single.addEventListener('click', () => {
          this.state.view = 'single';
          this.renderOffer();
        });
      } else {
        single.remove();
      }

      const stock = view.querySelector('[data-bd-stock]');
      if (products.every((product) => product.available)) {
        stock.append(el('strong', null, `${this.config.stockInLabel} `), document.createTextNode(this.config.stockInDetail));
      } else {
        stock.textContent = this.config.stockOut;
      }

      return view;
    }

    renderSingles() {
      const view = this.clone('single');
      const rows = view.querySelector('[data-bd-rows]');

      this.products.forEach((product, index) => {
        const row = this.clone('row');
        const img = row.querySelector('[data-bd-row-img]');
        if (product.image) {
          img.src = product.image;
          img.alt = product.name;
        } else {
          img.remove();
        }
        this.setBadge(row, index + 1);
        row.querySelector('[data-bd-row-name]').textContent = product.name;
        row.querySelector('[data-bd-row-sub]').textContent = product.subtitle || '';
        // List price only. A struck price per row would suggest the discount per product.
        row.querySelector('[data-bd-row-price]').textContent = this.money(product.price);

        const stepper = row.querySelector('.bd-quiz__stepper');
        const dec = row.querySelector('[data-bd-dec]');
        const inc = row.querySelector('[data-bd-inc]');
        const qtyEl = row.querySelector('[data-bd-qty]');
        const paint = () => {
          const qty = this.cartQty.get(product.variantId) || 0;
          qtyEl.textContent = String(qty);
          dec.disabled = qty === 0;
          inc.disabled = !product.available;
        };
        paint();

        const change = (delta) => {
          const qty = Math.max((this.cartQty.get(product.variantId) || 0) + delta, 0);
          stepper.classList.add('is-busy');
          this.setQty(product, qty).finally(() => {
            stepper.classList.remove('is-busy');
            paint();
          });
        };
        dec.addEventListener('click', () => change(-1));
        inc.addEventListener('click', () => change(1));
        rows.appendChild(row);
      });

      view.querySelector('[data-bd-back-bundle]').addEventListener('click', () => {
        this.state.view = 'bundle';
        this.renderOffer();
      });
      return view;
    }

    /* ---------------------------------------------------------------- pricing (Shopify Storefront API) */

    getPricing(products) {
      const lines = products.filter((product) => product.available);
      if (!lines.length) return Promise.resolve(null);
      const code = this.config.discountCode || '';
      const key = `${lines.map((product) => product.variantId).sort().join(',')}|${code}`;
      if (!this.pricingCache.has(key)) this.pricingCache.set(key, this.fetchPricing(lines, code));
      return this.pricingCache.get(key).then((pricing) => {
        // Do not cache failures: the next result page tries again.
        if (!pricing) this.pricingCache.delete(key);
        return pricing;
      });
    }

    async fetchPricing(lines, code) {
      try {
        const input = {
          lines: lines.map((product) => ({ merchandiseId: `gid://shopify/ProductVariant/${product.variantId}`, quantity: 1 })),
          discountCodes: code ? [code] : [],
        };
        const country = window.Shopify && window.Shopify.country;
        if (country) input.buyerIdentity = { countryCode: country };

        const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
        if (this.config.storefrontToken) headers['X-Shopify-Storefront-Access-Token'] = this.config.storefrontToken;

        const version = this.config.apiVersion || DEFAULT_API_VERSION;
        const response = await fetch(`/api/${version}/graphql.json`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ query: PRICING_QUERY, variables: { input } }),
        });
        if (!response.ok) throw new Error(`Storefront API ${response.status}`);
        const json = await response.json();
        const cart = json && json.data && json.data.cartCreate && json.data.cartCreate.cart;
        if (!cart || json.errors) throw new Error('Storefront API returned no cart');

        const toCents = (money) => Math.round(parseFloat(money.amount) * 100);
        const codeInfo = (cart.discountCodes || []).find((entry) => entry.code.toLowerCase() === code.toLowerCase());
        return {
          subtotal: toCents(cart.cost.subtotalAmount),
          total: toCents(cart.cost.totalAmount),
          codeApplicable: codeInfo ? codeInfo.applicable : null,
        };
      } catch (error) {
        console.warn('[bd-quiz] pricing unavailable', error);
        return null;
      }
    }

    /* ---------------------------------------------------------------- cart and discount */

    cartRequest(path, body) {
      return fetch(`${this.config.root}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          ...body,
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname,
        }),
      }).then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Cart request failed');
        return data;
      });
    }

    refreshCartQty() {
      return fetch(`${this.config.root}cart.js`, { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          this.cartQty = new Map(cart.items.map((item) => [item.variant_id, item.quantity]));
        })
        .catch(() => {});
    }

    async buyBundle(button, available) {
      button.disabled = true;
      try {
        const data = await this.cartRequest('cart/add.js', {
          items: available.map((product) => ({ id: product.variantId, quantity: 1 })),
        });
        await this.refreshCartQty();
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function') {
          // The drawer sits above the quiz (z-index 1000 vs 999).
          drawer.renderContents(data);
        } else {
          window.location.href = `${this.config.root}cart`;
        }
      } catch (error) {
        console.error('[bd-quiz]', error);
      } finally {
        button.disabled = false;
      }
    }

    setQty(product, qty) {
      this.cartQueue = this.cartQueue.then(async () => {
        try {
          const current = this.cartQty.get(product.variantId) || 0;
          if (qty === current) return;
          const data =
            current === 0
              ? await this.cartRequest('cart/add.js', { items: [{ id: product.variantId, quantity: qty }] })
              : await this.cartRequest('cart/change.js', { id: String(product.variantId), quantity: qty });
          await this.refreshCartQty();
          this.updateCartUi(data.sections);
        } catch (error) {
          console.error('[bd-quiz]', error);
        }
      });
      return this.cartQueue;
    }

    // Updates the header bubble and the drawer content without opening the drawer.
    updateCartUi(sections) {
      if (!sections) return;
      const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && sections['cart-icon-bubble']) {
        const source = parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
        if (source) bubble.innerHTML = source.innerHTML;
      }

      const drawerInner = document.querySelector('#CartDrawer');
      if (drawerInner && sections['cart-drawer']) {
        const source = parse(sections['cart-drawer']).querySelector('#CartDrawer');
        if (source) drawerInner.innerHTML = source.innerHTML;
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          const total = [...this.cartQty.values()].reduce((sum, qty) => sum + qty, 0);
          drawer.classList.toggle('is-empty', total === 0);
        }
      }
    }

    // The code is applied once; it stays valid when the quiz is repeated.
    applyDiscount() {
      if (this.discountRequested || !this.config.discountCode) return;
      this.discountRequested = true;
      const code = encodeURIComponent(this.config.discountCode);
      const redirect = encodeURIComponent(`${this.config.root}cart.js`);
      fetch(`${this.config.root}discount/${code}?redirect=${redirect}`, { credentials: 'same-origin' }).catch((error) => {
        this.discountRequested = false;
        console.error('[bd-quiz]', error);
      });
    }
  }

  /* ------------------------------------------------------------------ boot */

  let quiz = null;

  // Any element with data-bd-quiz-open starts the quiz at question 1 (hero button, menu button, ...).
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-bd-quiz-open]');
    if (!trigger || !quiz) return;
    event.preventDefault();
    quiz.open(trigger, event);
  });

  if (document.querySelector('[data-bd-quiz]')) quiz = new BDQuiz();
})();
